import React, { useMemo } from 'react';

import { useLeadLabels, usePropertyLabels } from 'src/services/api';
import AutocompleteMultiple from 'src/components/input/autocomplete-multiple/AutocompleteMultiple';

type Props = Omit<React.ComponentProps<typeof AutocompleteMultiple>, 'options'> & {
    type: 'lead' | 'property',
    // When set, only these label IDs (as strings) are offered as options. Used to narrow the
    // choices to a subset (e.g. the labels allowed by an inbox filter).
    allowedValues?: string[],
    // When set, these label IDs (as strings) are removed from the offered options. Used to keep
    // the "is" and "is not" pickers disjoint (a label chosen in one cannot be offered in the other).
    excludeValues?: string[],
}

export default function SelectLabels({
    value: propsValue,
    type,
    allowedValues,
    excludeValues,
    ...props
}: Props) {
    const { data: leadLabels } = useLeadLabels();
    const { data: propertyLabels } = usePropertyLabels();

    const options = useMemo(() => {
        if (!leadLabels || !propertyLabels) {
            return [];
        }

        const allowed = allowedValues ? new Set(allowedValues) : undefined;
        const excluded = excludeValues ? new Set(excludeValues) : undefined;
        const labels = type === 'lead' ? leadLabels : propertyLabels;
        return labels
            .filter((label) => !allowed || allowed.has(String(label.id)))
            .filter((label) => !excluded || !excluded.has(String(label.id)))
            .map((label) => ({
                value: String(label.id),
                label: label.name,
            }));
    }, [type, leadLabels, propertyLabels, allowedValues, excludeValues]);

    const value = propsValue ? propsValue.map(String) : propsValue;

    return (
        <AutocompleteMultiple {...props} value={value} options={options} disabled={props.disabled || options.length === 0} />
    );
}
